import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Plus, Copy, Trash2, Loader2, Pencil } from 'lucide-react'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import RoleMatrixEditor, { RoleForEditor } from './RoleMatrixEditor'
import RolesTabSkeleton from './RolesTabSkeleton'

const RolesTab: React.FC = () => {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(Query.PROJECT_ROLES)
  const roles: RoleForEditor[] = data?.projectRoles ?? []
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = roles.find((r) => r.id === selectedId) ?? roles[0] ?? null

  const [createRole, { loading: creating }] = useMutation(Mutation.CREATE_PROJECT_ROLE)
  const [renameRole, { loading: renaming }] = useMutation(Mutation.UPDATE_PROJECT_ROLE)
  const [deleteRole, { loading: deleting }] = useMutation(Mutation.DELETE_PROJECT_ROLE)

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [copyFrom, setCopyFrom] = useState<string>('none')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reassignTo, setReassignTo] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)

  if (loading && !data) return <RolesTabSkeleton />

  const run = async (fn: () => Promise<any>, close: () => void) => {
    setSubmitting(true)
    try {
      await fn()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
      setSubmitting(false)
      return
    }
    close()
    try { await refetch() } catch { /* list refresh is best-effort; the mutation succeeded */ }
    setSubmitting(false)
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">{t('Roles_list_title', 'Roles')}</span>
          <Button size="sm" className="h-8" onClick={() => { setNewName(''); setCopyFrom('none'); setCreateOpen(true) }}>
            <Plus className="h-4 w-4" />
            {t('Roles_new', 'New role')}
          </Button>
        </div>
        <div className="rounded-md border border-border divide-y divide-border">
          {roles.map((r) => (
            <button key={r.id} type="button"
              className={cn('w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                selected?.id === r.id && 'bg-accent')}
              onClick={() => setSelectedId(r.id)}>
              <span className="truncate">{r.key ? t('role_' + r.key, r.name) : r.name}</span>
              {r.isSystem && <Badge variant="secondary" className="ml-auto">{t('Roles_system_badge', 'System')}</Badge>}
              <span className={cn('text-xs text-muted-foreground', !r.isSystem && 'ml-auto')}>{r.memberCount}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        {selected && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Button variant="outline" size="sm" className="h-8"
                onClick={() => { setRenameValue(selected.name); setRenameOpen(true) }}>
                <Pencil className="h-4 w-4" />
                {t('Roles_rename', 'Rename')}
              </Button>
              <Button variant="outline" size="sm" className="h-8" disabled={creating || submitting}
                onClick={() => run(
                  () => createRole({ variables: { name: `${selected.name} ${t('Roles_copy_suffix', '(copy)')}`, copyFromRoleId: selected.id } }),
                  () => {})}>
                {creating || submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
                {t('Roles_duplicate', 'Duplicate')}
              </Button>
              {!selected.isSystem && (
                <Button variant="destructive" size="sm" className="h-8"
                  onClick={() => { setReassignTo(''); setDeleteOpen(true) }}>
                  <Trash2 className="h-4 w-4" />
                  {t('delete', 'Delete')}
                </Button>
              )}
            </div>
            <RoleMatrixEditor key={selected.id} role={selected} onSaved={refetch} />
          </>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('Roles_new', 'New role')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder={t('Roles_name_placeholder', 'Role name')} value={newName}
              onChange={(e) => setNewName(e.target.value)} />
            <Select value={copyFrom} onValueChange={setCopyFrom}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('Roles_copy_none', 'Start empty')}</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {t('Roles_copy_from', 'Copy from')}: {r.key ? t('role_' + r.key, r.name) : r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('cancel', 'Cancel')}</Button>
            <Button disabled={!newName.trim() || creating || submitting}
              onClick={() => run(
                () => createRole({ variables: { name: newName.trim(), copyFromRoleId: copyFrom === 'none' ? null : copyFrom } }),
                () => setCreateOpen(false))}>
              {(creating || submitting) && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('create', 'Create')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('Roles_rename', 'Rename')}</DialogTitle></DialogHeader>
          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setRenameOpen(false)}>{t('cancel', 'Cancel')}</Button>
            <Button disabled={!renameValue.trim() || renaming || submitting}
              onClick={() => run(
                () => renameRole({ variables: { id: selected!.id, name: renameValue.trim() } }),
                () => setRenameOpen(false))}>
              {(renaming || submitting) && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('Roles_delete_title', 'Delete role')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('Roles_delete_reassign_hint', 'Members with this role will be moved to the selected role.')}
          </p>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger className="h-9"><SelectValue placeholder={t('Roles_reassign_placeholder', 'Reassign members to…')} /></SelectTrigger>
            <SelectContent>
              {roles.filter((r) => r.id !== selected?.id).map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.key ? t('role_' + r.key, r.name) : r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('cancel', 'Cancel')}</Button>
            <Button variant="destructive" disabled={!reassignTo || deleting || submitting}
              onClick={() => run(
                () => deleteRole({ variables: { id: selected!.id, reassignToRoleId: reassignTo } }),
                () => { setDeleteOpen(false); setSelectedId(null) })}>
              {(deleting || submitting) && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('delete', 'Delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RolesTab
